<template>
  <permission-guard situation='site_configuration'>
    <v-form ref='SettingsBasicForm'>
      <v-row>
        <v-col cols='6'>
          <v-text-field
            v-model='site.siteKey'
            class='mb-3'
            label='サイトキー（変更不可）'
            disabled
            style='width: 300px'
            required
          />
          <v-text-field
            v-model='site.name'
            class='mb-2'
            label='サイト名'
            :rules='[presenceValidation]'
            style='width: 300px'
            required
          />
          <v-text-field
            v-model='site.slogan'
            class='mb-2'
            label='キャッチコピー'
            style='width: 400px'
            required
          />
          <v-textarea
            v-model='site.description'
            class='mb-2 body-2'
            label='サイト説明文（メタディスクリプション）'
            style='width: 400px'
            outlined
            rows='3'
            required
          />
          <v-text-field
            v-model='site.itemCntPerPage'
            class='mb-3'
            label='1ページあたりのアイテム数'
            type='number'
            style='width: 300px'
            :rules='[presenceValidation]'
            :min='0'
          />
          <v-switch
            v-model='site.isIndex'
            persistent-hint
            class='p-settingForm--inputSwitch'
            hint='※インデックスを必ず保証するものではありません'
            label='サイト全体をインデックスする'
          />
          <v-switch
            v-model='site.showNoindexPost'
            persistent-hint
            class='p-settingForm--inputSwitch'
            hint='※noindex記事がサイト内に表示されるようになる'
            label='サイト内にnoindex記事が表示される'
          />
          <v-switch
            v-model='site.cachePost'
            persistent-hint
            class='p-settingForm--inputSwitch'
            hint='※記事をキャッシュすることで表示速度を向上させます'
            label='記事をキャッシュする'
          />
          <v-switch
            v-model='site.isPremiumDisplay'
            persistent-hint
            class='p-settingForm--inputSwitch'
            hint='※記事ページが景品表示法に対応できるようになります。'
            label='景品表示法を有効にする'
          />
          <v-switch
            v-model='site.postPremiumDisplayEnabled'
            persistent-hint
            class='p-settingForm--inputSwitch'
            hint='※すべて記事に景品表示法の文言が表示するようになります。'
            label='すべての記事に表示'
          />
          <v-textarea
            v-model='site.premiumDisplayMessage'
            class='mb-2 caption'
            label='景品表示法文言'
            outlined
            rows='3'
            required
          />
          <v-select
            v-model='site.postsOrder'
            label='新着記事の表示順'
            :items='postsOrders'
            item-value='value'
            item-text='name'
            hint='※新着記事一覧で日付が新しい順で表示されます'
            persistent-hint
            style='width: 300px'
          />
          <v-select
            v-model='site.dateFormat'
            label='日付形式'
            :items='dateFormats'
            item-value='value'
            item-text='name'
            hint='記事の日付表示形式を選択してください'
            persistent-hint
            style='width: 300px'
          />
          <v-row>
            <v-text-field
              label="利用規約URL"
              v-model="site.termUrl"
            ></v-text-field>
            <v-btn
              class='mt-4 ml-2 mr-5 white--text'
              color="primary"
              small
              @click="openMediaPageDialog('termUrl')"
            >固定ページから選択する</v-btn>
          </v-row>
          <v-row>
            <v-text-field
              label="会社情報URL"
              v-model="site.companyInfoUrl"
            ></v-text-field>
            <v-btn
              class='mt-4 ml-2 mr-5 white--text'
              color="primary"
              small
              @click="openMediaPageDialog('companyInfoUrl')"
            >固定ページから選択する</v-btn>
          </v-row>
          <v-row>
            <v-text-field
              label="プライバシーポリシーURL"
              v-model="site.privacyPolicyUrl"
            ></v-text-field>
            <v-btn
              class='mt-4 ml-2 mr-5 white--text'
              color="primary"
              small
              @click="openMediaPageDialog('privacyPolicyUrl')"
            >固定ページから選択する</v-btn>
          </v-row>
        </v-col>
        <v-col cols='6'>
          <v-textarea
            v-model='site.adsTxt'
            class='mb-2 caption'
            label='ads.txt'
            outlined
            rows='3'
            required
            placeholder='広告の設定に必要な場合は入力してください'
          />
          <v-textarea
            v-model='site.robotsTxt'
            class='mb-2 caption'
            label='robots.txt'
            outlined
            rows='3'
            required
            placeholder='クローラーの設定を入力してください'
          />
          <div class='mb-5'>
            <ImageFormAndThumbnail
              label-name='ロゴ'
              :propsImagePreviewURL='site.logo.url'
              @change-image-file='(value) => site.logo = value'
            ></ImageFormAndThumbnail>
          </div>
          <div class='mb-5'>
            <ImageFormAndThumbnail
              label-name='フッターロゴ'
              :propsImagePreviewURL='site.footerLogo.url'
              @change-image-file='(value) => site.footerLogo = value'
              hint='※flattyテンプレートを使用しているメディアで、フッター専用のロゴを設定したい場合はこちらに画像アップして下さい。'
            ></ImageFormAndThumbnail>
          </div>
          <div class='mb-2'>
            <ImageFormAndThumbnail
              label-name='ファビコン'
              :propsImagePreviewURL='site.favicon.url'
              @change-image-file='(value) => site.favicon = value'
            ></ImageFormAndThumbnail>
          </div>
          <v-switch
            v-model='site.displayWriterProfile'
            persistent-hint
            class='p-settingForm--inputSwitch'
            hint='※記事にライターの情報が表示されます'
            label='ライタープロフィールを表示する'
          />
        </v-col>
      </v-row>
    </v-form>
    <v-spacer />
    <v-alert dense outlined type='error' style='width: 400px'>
      <span class='caption'>
        公開中のサイトの基本情報やドメインを変更した場合、SEOに影響が及ぶことがあります
      </span>
    </v-alert>
    <v-btn color='green darken-1' tile dark depressed @click='updateSite'>
      更新
    </v-btn>
    <v-dialog v-model='mediaPageDialog' width='800'>
      <v-card>
        <v-card-title>固定ページ選択</v-card-title>
        <v-card-text>
          <v-simple-table>
            <thead>
              <tr>
                <th>タイトル</th>
                <th>slug</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for='(mediaPage, index) in mediaPages' :key='index'>
                <td>{{ mediaPage.title }}</td>
                <td>{{ mediaPage.slug }}</td>
                <td>
                  <v-btn
                    tile
                    color="success"
                    class="ma-2"
                    @click="selectMediaPage(mediaPage)"
                  >
                    選択
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </permission-guard>
</template>
<script>
import axios from 'axios'
import PermissionGuard from '@/components/commons/PermissionGuard'
import formValidationUtils from '@/mixins/form_validation_utils'
import ImageFormAndThumbnail from '@/components/commons/ImageFormAndThumbnail'
import { deserialize } from 'deserialize-json-api'
import Qs from 'qs'
import urlUtils from '@/mixins/url_utils'
import _ from 'lodash'

export default {
  components: {
    PermissionGuard,
    ImageFormAndThumbnail
  },
  mixins: [formValidationUtils, urlUtils],
  props: {
    siteId: {
      type: Number,
      required: true
    }
  },
  data: () => {
    return {
      site: {},
      postsOrders: [
        {
          name: '更新日',
          value: 'updated_at'
        },
        {
          name: '公開日',
          value: 'released_on'
        }
      ],
      dateFormats: [
        {
          name: '2023.12.31  y.m.d',
          value: 'dot'
        },
        {
          name: '2023年12月31日  y年m月d日',
          value: 'kanji'
        },
        {
          name: '2023/12/31  y/m/d',
          value: 'slash'
        }
      ],
      mediaPageDialog: false,
      mediaPages: [],
      mediaPageDialogOf: ''
    }
  },
  mounted: function () {
    this.fetchSite()
    this.fetchMediaPage()
  },
  methods: {
    fetchSite () {
      axios.get(`/api/sites/${this.siteId}/edit`).then(
        ({ data }) => {
          this.site = data.data.attributes
        },
        (error) => {
          const payload = {
            status: error.response.status,
            message: error.response.data.errors.toString()
          }
          this.$store.dispatch('SystemMessageStore/createMessage', payload)
        }
      )
    },
    updateSite () {
      if (!this.$refs.SettingsBasicForm.validate()) return false

      let formData = new FormData()
      Object.keys(this.site).forEach((key) => {
        if (this.site[key] !== null) {
          formData.append(`site[${_.snakeCase(key)}]`, this.site[key])
        }
      })
      formData.append('_method', 'PATCH')
      axios.post(`/api/sites/${this.site.id}`, formData).then(
        () => {
          const payload = {
            status: 200,
            message: '変更を保存しました'
          }
          this.$store.dispatch('SystemMessageStore/createMessage', payload)
        },
        (error) => {
          const payload = {
            status: error.response.status,
            message: error.response.data.errors.toString()
          }
          this.$store.dispatch('SystemMessageStore/createMessage', payload)
        }
      )
    },
    openMediaPageDialog (mediaPageDialogOf) {
      this.mediaPageDialogOf = mediaPageDialogOf
      this.mediaPageDialog = true
    },
    fetchMediaPage () {
      axios
        .get(`/api/media_pages`, {
          params: { q: { is_published_eq: 1 } },
          paramsSerializer: function (params) {
            return Qs.stringify(params, { arrayFormat: 'brackets' })
          }
        })
        .then(
          ({ data }) => {
            this.mediaPages = deserialize(data).data
          },
          (error) => {
            const payload = {
              status: error.response.status,
              message: error.response.data.errors.toString()
            }
            this.$store.dispatch('SystemMessageStore/createMessage', payload)
          }
        )
    },
    selectMediaPage (mediaPage) {
      const url = this.site.initialDomainWithProtocol + this.mediaRootPath() + 'pages/' + mediaPage.slug
      this.site[this.mediaPageDialogOf] = url
      this.mediaPageDialog = false
    }
  }
}
</script>

<style scoped></style>
